import _extends from "@babel/runtime/helpers/extends";
import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/objectWithoutPropertiesLoose";
var _excluded = ["maxvalue"];
import React from 'react';
import { default as MuiBadge } from '@mui/material/Badge';
import makeStyles from './../makeStyles/makeStylesInternal';
import PropTypes from 'prop-types';
import { spacing20, spacing40, sizingLarge, sizingXLarge, borderRadiusLarge, borderWidthThin, colorGlobalBorderDefault } from '../styles/tokens';
var useStyles = makeStyles(function (theme) {
  return {
    badge: {
      color: theme.palette.status.default.text,
      background: theme.palette.grey[100],
      minWidth: sizingXLarge,
      width: 'auto',
      height: sizingLarge,
      fontSize: theme.typography.body2.fontSize,
      position: 'initial',
      marginLeft: spacing40,
      marginTop: "-" + spacing20,
      padding: '0 0.75rem',
      border: borderWidthThin + " solid " + colorGlobalBorderDefault,
      borderRadius: borderRadiusLarge,
      transform: 'none'
    }
  };
});

/**
 * Use `Badge` to indicate a numerical value or count.
 * @done true
 * @updated v7.3.0
 * @versionAdded v0.0.6
 * @examples
 *  BadgeExamples
 *  NotificationBadgeExamples
 */
var Badge = /*#__PURE__*/React.forwardRef(function (props, ref) {
  var maxvalue = props.maxvalue,
    rest = _objectWithoutPropertiesLoose(props, _excluded);
  var classes = useStyles(props);
  var classOverrides = {
    badge: classes.badge
  };
  return /*#__PURE__*/React.createElement(MuiBadge, _extends({}, rest, {
    max: maxvalue,
    classes: classOverrides,
    ref: ref,
    overlap: "rectangular" // this addresses a bug in MUI, where the internal default value ("rectangle") is deprecated
    ,
    showZero: true
    // if a badge has no content, the invisible flag is automatically
    // set to 'true'; we never want a badge to be invisible, so make sure it's
    // always false
    ,
    invisible: false
  }));
});
Badge.muiName = 'Badge';
Badge.propTypes = {
  /**
   * @ignore
   *
   * The anchor of the badge.
   * @default {
   *   vertical: 'top',
   *   horizontal: 'right',
   * }
  */
  anchorOrigin: PropTypes.shape({
    horizontal: PropTypes.oneOf(['left', 'right']).isRequired,
    vertical: PropTypes.oneOf(['bottom', 'top']).isRequired
  }),
  /**
   * The content rendered within the badge.
   */
  badgeContent: PropTypes.number.isRequired,
  /**
   * The badge will be added relative to this node.
   */
  children: PropTypes.node.isRequired,
  /**
   * Override or extend the styles applied to the component.
   */
  classes: PropTypes.object,
  /**
   * @ignore
   *
   * The color of the component.
   * It supports both default and custom theme colors, which can be added as shown in the
   * [palette customization guide](https://mui.com/material-ui/customization/palette/#adding-new-colors).
   * @default 'default'
   */
  color: PropTypes.oneOfType([PropTypes.oneOf(['default', 'primary', 'secondary', 'error', 'info', 'success', 'warning']), PropTypes.string]),
  /**
   * The component used for the root node. Either a string to use a DOM element or a component.
   */
  component: PropTypes.oneOf(['string', 'func']),
  /**
   * @ignore
   *
   * The components used for each slot inside the Badge.
   * Either a string to use a HTML element or a component.
   * @default {}
   */
  components: PropTypes.shape({
    Badge: PropTypes.elementType,
    Root: PropTypes.elementType
  }),
  /**
   * @ignore
   *
   * The props used for each slot inside the Badge.
   * @default {}
   */
  componentsProps: PropTypes.shape({
    badge: PropTypes.oneOfType([PropTypes.func, PropTypes.object]),
    root: PropTypes.oneOfType([PropTypes.func, PropTypes.object])
  }),
  /**
   * @ignore
   *
   * If `true`, the badge is invisible.
   */
  invisible: PropTypes.bool,
  /**
   * Max value of content rendered within the badge.
   */
  maxvalue: PropTypes.number,
  /**
   * @ignore
   *
   * Wrapped shape the badge should overlap.
   * @default 'rectangular'
   */
  overlap: PropTypes.oneOf(['circular', 'rectangular']),
  /**
   * @ignore
   *
   * Controls whether the badge is hidden when `badgeContent` is zero.
   */
  showZero: PropTypes.bool,
  /**
   * The system prop that allows defining system overrides as well as additional CSS styles.
   */
  sx: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.func, PropTypes.object, PropTypes.bool])), PropTypes.func, PropTypes.object]),
  /**
   * @ignore
   * Variant of the badge
   */
  variant: PropTypes.oneOfType([PropTypes.oneOf(['dot', 'standard']), PropTypes.string])
};
Badge.displayName = 'Badge';
Badge.defaultProps = {
  maxvalue: 999
};
export default Badge;